/**
 * DEMOLISH LAYERS (2026-10-14, docs/bim-plan/32 §3 E; Matt: "a new tool under finishes that
 * worked like trowel but it was a demo tool … hover over the elements and it would highlight to
 * tell you what it is and you would click to mark it as demo in this phase. Then, when the demo
 * tool is activated you see little markers over items marked for demo so you know where you click
 * to undemo them"). The pure helpers the tool and its markers share: the phase the 3D view is in,
 * whether a wall can take demolition there, the face of the layer a click would take off (a
 * highlight), and where each mark's marker sits.
 *
 * The phase is the live 3D view's phase (the Phase card) — the same rule the drawing tools use to
 * stamp new work. The mark itself is the wall's scope (kernel scope.ts), so the Scope list, the
 * plan's demo hatch and the peeled 3D all read the one record.
 */
import {
  resolveElement, listElements, listPhases, getMaterial, centerlineOffsetFt, offsetPolyline, flattenPathPts,
  wallFaceOffsets, wallDemoMarks, faceLayerOrder, faceLayerGroups, wallLayerKeys, migrateWallScope, scopeVerbOf, normalizeScope, viewCommands,
  roofDemoMarks, roofPlaneVerb,
} from '@aj/bim-kernel'
import { wallZRange } from '@aj/bim-geometry/categories/wall/generate3d'
import { roofSkeletonFor } from '@aj/bim-geometry/categories/roof/skeleton'

/** the phase the 3D view shows, if it is one demolition can happen in (not the first phase) */
export function demoPhase(model) {
  if (!model) return { error: 'No model loaded' }
  const phases = listPhases(model).slice().sort((a, b) => a.order - b.order)
  if (phases.length < 2) return { error: 'This model has one phase. Add a phase before marking demolition' }
  const id = viewCommands.getLive3dPhase(model).phase || null
  if (!id) return { error: 'Set the 3D view to the phase the work happens in (the Phase card). Demolition is marked in that phase' }
  const ph = phases.find((p) => p.id === id)
  if (!ph) return { error: 'The 3D view\'s phase no longer exists. Pick one on the Phase card' }
  if (ph.id === phases[0].id) return { error: `${ph.name} is the first phase, so nothing comes off yet. Set the 3D view to ${phases[1].name}` }
  return { id: ph.id, name: ph.name, order: ph.order, phases }
}

/** can this wall take a layer demolition in `ph`? null = yes, else the reason it can't */
export function wallRefusal(model, el, ph) {
  const order = (id) => ph.phases.find((p) => p.id === id)?.order
  if (el.phaseCreated && (order(el.phaseCreated) ?? -Infinity) >= ph.order) return `This wall is new in ${ph.phases.find((p) => p.id === el.phaseCreated)?.name ?? 'this phase'}, so there is nothing existing to take off`
  if (el.phaseDemolished && (order(el.phaseDemolished) ?? Infinity) <= ph.order) return 'This whole wall is demolished, so its layers go with it'
  const sc = normalizeScope(el.params?.scope)
  if (sc && sc.phase !== ph.id && Object.keys(sc.parts).length) {
    return `This wall's work is marked in ${ph.phases.find((p) => p.id === sc.phase)?.name ?? 'another phase'}. Set the 3D view to that phase to change it`
  }
  return null
}

/**
 * THE TROWEL IN A LATER PHASE (DEMOLISH LAYERS round 2, 2026-10-14; Matt: "shouldn't the wall then
 * show in the new construction phase peeled back … and then the trowel could be used once more").
 * With the 3D view in a phase after an existing wall's own, a finish troweled onto that wall is
 * NEW WORK in that phase (the wall's scope, `scope.add`) and the existing finish stays what it was
 * in the earlier phase. Returns how the trowel treats this wall face:
 *   { mode: 'paint' }            as always (no phases, the first phase, or new work in this phase)
 *   { mode: 'scope', ph }        record the new finish in the view's phase
 *   { mode: 'refuse', why }      the old finish is still on that face, or the wall is gone
 */
export function trowelMode(model, el, side) {
  const ph = demoPhase(model)
  if (ph.error) return { mode: 'paint' }
  const order = (id) => ph.phases.find((p) => p.id === id)?.order
  if (el.phaseCreated && (order(el.phaseCreated) ?? -Infinity) >= ph.order) return { mode: 'paint' }
  const why = wallRefusal(model, el, ph)
  if (why) return { mode: 'refuse', why }
  // the face's outer finish must be off first: the new one replaces it, never covers it
  const first = faceLayerGroups(el.assembly, side)[0]
  if (first && el.assembly.layers[first[0]]?.function === 'finish') {
    const scope = migrateWallScope(normalizeScope(el.params?.scope), el.assembly)
    const keys = wallLayerKeys(el.assembly)
    const still = first.some((i) => scopeVerbOf(scope, keys[i]) === 'keep')
    if (still) {
      const name = materialName(model, el.assembly.layers[first[0]].materialId)
      return { mode: 'refuse', why: `The existing ${name} is still on this face in ${ph.name}. Take it off first with Demolish Layers, or set the 3D view to ${ph.phases[0].name} to correct the existing finish` }
    }
  }
  return { mode: 'scope', ph }
}

const materialName = (model, id) => (id && getMaterial(model, id)?.name) || 'Layer'

// ---------- ROOF LAYERS (2026-10-30, Matt: "I think we need it to act like walls") ----------

/** can this roof take a layer demolition in `ph`? null = yes, else the reason it can't */
export function roofRefusal(model, el, ph) {
  const order = (id) => ph.phases.find((p) => p.id === id)?.order
  if (el.phaseCreated && (order(el.phaseCreated) ?? -Infinity) >= ph.order) return `This roof is new in ${ph.phases.find((p) => p.id === el.phaseCreated)?.name ?? 'this phase'}, so there is nothing existing to take off`
  if (el.phaseDemolished && (order(el.phaseDemolished) ?? Infinity) <= ph.order) return 'This whole roof is demolished, so its layers go with it'
  const sc = normalizeScope(el.params?.scope)
  if (sc && sc.phase !== ph.id && (Object.keys(sc.parts).length || Object.keys(sc.add ?? {}).length || Object.keys(sc.planes ?? {}).length)) {
    return `This roof's work is marked in ${ph.phases.find((p) => p.id === sc.phase)?.name ?? 'another phase'}. Set the 3D view to that phase to change it`
  }
  return null
}

/** the roofing material(s) on a roof's planes (or on one plane, `nodeId`), for labels */
function roofingName(model, el, nodeId) {
  const nodes = (el.params?.boundary?.nodes ?? []).filter((n) => !nodeId || n.id === nodeId)
  const ids = [...new Set(nodes.map((n) => n.edge?.finish).filter(Boolean))]
  return ids.length === 1 ? materialName(model, ids[0]) : 'Roofing'
}

/** what a roof target is called on the status line and the marker (`nodeId`: the plane) */
export function roofTargetLabel(model, el, target, nodeId) {
  if (target.kind === 'structure') return 'the rafters'
  if (target.keys.includes('roofing')) return `${roofingName(model, el, nodeId)} (with its underlayment)`
  const sub = el.assembly?.layers?.[0]
  return sub ? materialName(model, sub.materialId) : 'Sheathing'
}

/**
 * The trowel on a roof plane with the 3D view in a later phase than an existing roof's own: the
 * new roofing is NEW WORK in that phase (`scope.add.roofing`), never a repaint of the existing
 * roofing, and only once the existing roofing is marked to come off (Leave it off).
 *   { mode: 'paint' } | { mode: 'scope', ph } | { mode: 'refuse', why }
 */
export function roofTrowelMode(model, el, nodeId) {
  const ph = demoPhase(model)
  if (ph.error) return { mode: 'paint' }
  const order = (id) => ph.phases.find((p) => p.id === id)?.order
  if (el.phaseCreated && (order(el.phaseCreated) ?? -Infinity) >= ph.order) return { mode: 'paint' }
  const why = roofRefusal(model, el, ph)
  if (why) return { mode: 'refuse', why }
  // by PLANE (2026-11-02): the plane under the trowel (or, for the whole roof, any plane)
  const sc = normalizeScope(el.params?.scope)
  const ids = nodeId ? [nodeId] : (el.params?.boundary?.nodes ?? []).map((n) => n.id).filter(Boolean)
  const vs = ids.length ? ids.map((id) => roofPlaneVerb(sc, id, 'roofing')) : [scopeVerbOf(sc, 'roofing')]
  if (vs.includes('remove')) return { mode: 'scope', ph, planes: ids.filter((id, i) => vs[i] === 'remove') }
  const v = vs.includes('replace') ? 'replace' : 'keep'
  const where = nodeId ? 'this roof plane' : 'this roof'
  if (v === 'keep') return { mode: 'refuse', why: `The existing ${roofingName(model, el, nodeId)} is still on ${where} in ${ph.name}. Take it off first with Demolish Layers, or set the 3D view to ${ph.phases[0].name} to correct the existing roofing` }
  if (v === 'replace') return { mode: 'refuse', why: `The roofing on ${where} is set to go back the same in ${ph.name}. Choose Leave it off on its tag (Demolish Layers) to roof it with something new` }
  return { mode: 'scope', ph }
}

/** a roof plane's plan area (its skeleton faces), 0 when it has none */
export function roofPlaneArea(el, nodeId) {
  const ei = (el.params?.boundary?.nodes ?? []).findIndex((n) => n.id === nodeId)
  let total = 0
  for (const f of roofSkeletonFor(el)?.faces ?? []) {
    if (f.edgeIndex !== ei || !f.ring || f.ring.length < 3) continue
    let a = 0
    for (let i = 0, j = f.ring.length - 1; i < f.ring.length; j = i++) a += f.ring[j][0] * f.ring[i][1] - f.ring[i][0] * f.ring[j][1]
    total += Math.abs(a) / 2
  }
  return total
}

/** where a roof's markers stand: over its largest plane (or over plane `nodeId`), on the roof's top */
export function roofMarkerPoint(el, nodeId) {
  const sk = roofSkeletonFor(el)
  const ei = nodeId ? (el.params?.boundary?.nodes ?? []).findIndex((n) => n.id === nodeId) : -1
  let best = null, area = -1
  for (const f of sk?.faces ?? []) {
    if (!f.ring || f.ring.length < 3) continue
    if (nodeId && f.edgeIndex !== ei) continue
    let a = 0
    for (let i = 0, j = f.ring.length - 1; i < f.ring.length; j = i++) a += f.ring[j][0] * f.ring[i][1] - f.ring[i][0] * f.ring[j][1]
    if (Math.abs(a) > area) { area = Math.abs(a); best = f }
  }
  if (!best) return null
  const c = best.ring.reduce((s, p) => [s[0] + p[0] / best.ring.length, s[1] + p[1] / best.ring.length], [0, 0])
  const pl = best.plane
  const z = pl.zOriginFt + pl.risePerRun * ((c[0] - pl.origin[0]) * pl.uphill[0] + (c[1] - pl.origin[1]) * pl.uphill[1])
  const t = (el.assemblyThicknessFt || 0) * Math.sqrt(1 + pl.risePerRun * pl.risePerRun)
  return [c[0], c[1], z + t + 0.5]
}

/** signed offset from the CENTERLINE to the outer face of a group of layers on one face */
function groupFaceOffset(el, face, indexes) {
  const faces = wallFaceOffsets(el.params.flipped, el.assembly)
  const off = face === 'exterior' ? faces.exteriorOff : faces.interiorOff
  const sign = Math.sign(off || (face === 'exterior' ? 1 : -1))
  const layers = el.assembly?.layers ?? []
  let before = 0
  for (const i of faceLayerOrder(el.assembly, face)) {
    if (indexes.includes(i)) break
    before += layers[i]?.thicknessFt || 0
  }
  return { off: off - sign * before, sign }
}

/** a highlight quad on the wall at a signed offset from its centerline (just proud of it) */
function faceQuad(el, off, sign) {
  const path = el.params?.path
  if (!path?.nodes?.length || !el.assembly) return null
  const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
  const fp = offsetPolyline(path, delta + off + sign * 0.03)
  const pts = fp ? flattenPathPts(fp) : []
  const zr = wallZRange(el)
  if (pts.length < 2 || !zr) return null
  const pos = [], idx = []
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], k = pos.length / 3
    pos.push(a[0], a[1], zr.z0, b[0], b[1], zr.z0, b[0], b[1], zr.z1, a[0], a[1], zr.z1)
    idx.push(k, k + 1, k + 2, k, k + 2, k + 3, k, k + 2, k + 1, k, k + 3, k + 2)
  }
  return { positions: new Float32Array(pos), indices: new Uint32Array(idx) }
}

/** the highlight for what a click on this face would take off */
export function targetHighlight(el, target) {
  if (target.kind === 'layers') {
    const { off, sign } = groupFaceOffset(el, target.face, target.indexes)
    return faceQuad(el, off, sign)
  }
  // insulation / studs: the core face on that side (every layer of the face is off by now)
  const { off, sign } = groupFaceOffset(el, target.face, [])
  return faceQuad(el, off, sign)
}

/** what a target is called on the status line and the marker */
export function targetLabel(model, el, target) {
  if (target.kind === 'insulation') return 'the insulation in the studs'
  if (target.kind === 'studs') return 'the studs'
  const layers = el.assembly?.layers ?? []
  const names = target.indexes.map((i) => materialName(model, layers[i]?.materialId))
  return names.length > 1 ? `${names[0]} (with its ${names.slice(1).join(', ').toLowerCase()})` : names[0]
}

/** a point along a polyline at half its length */
function midOf(pts) {
  let L = 0
  for (let i = 0; i + 1 < pts.length; i++) L += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
  let t = L / 2
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], d = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (t <= d || i + 2 === pts.length) { const f = d ? Math.min(1, t / d) : 0; return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f] }
    t -= d
  }
  return pts[0]
}

/**
 * Every marker the tool shows in phase `ph`: one per marked group on each wall whose work is in
 * that phase, standing just off the face it came off (the insulation at the wall's middle), half
 * way along the wall and half way up. `stack` numbers markers sharing a face so they don't overlap.
 */
export function demoMarkers(model, ph) {
  const out = []
  if (!model || !ph?.id) return out
  for (const rec of listElements(model)) {
    if (rec.category === 'roof') {
      const sc = normalizeScope(rec.params?.scope)
      if (!sc || sc.phase !== ph.id) continue
      const el = resolveElement(model, rec.id)
      if (!el) continue
      // by PLANE (2026-11-02): each plane's tags stand over that plane
      const stacks = new Map()
      const at = (nodeId) => {
        const p = roofMarkerPoint(el, nodeId) ?? roofMarkerPoint(el)
        const k = nodeId || ''
        const n = stacks.get(k) ?? 0
        stacks.set(k, n + 1)
        return p ? { world: p, stack: n } : null
      }
      for (const [nodeId, mat] of Object.entries(sc.add?.roofing ?? {})) {
        if (roofPlaneVerb(sc, nodeId, 'roofing') !== 'remove') continue
        const w = at(nodeId)
        if (w) out.push({ id: `${rec.id}|addroof:${nodeId}`, wallId: rec.id, roof: true, mark: { verb: 'add', keys: [], nodeIds: [nodeId], materialId: mat }, label: materialName(model, mat), ...w })
      }
      for (const mark of roofDemoMarks(el)) {
        const w = at(mark.nodeId)
        if (w) out.push({ id: `${rec.id}|${mark.nodeId || ''}|${mark.keys.join(',')}`, wallId: rec.id, roof: true, mark, label: roofTargetLabel(model, el, { kind: 'layers', keys: mark.keys }, mark.nodeId), ...w })
      }
      continue
    }
    if (rec.category !== 'wall') continue
    const sc = normalizeScope(rec.params?.scope)
    if (!sc || sc.phase !== ph.id || (!Object.keys(sc.parts).length && !Object.keys(sc.add ?? {}).length)) continue
    const el = resolveElement(model, rec.id)
    if (!el?.assembly || !el.params?.path?.nodes?.length) continue
    const zr = wallZRange(el)
    if (!zr) continue
    const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
    const faces = wallFaceOffsets(el.params.flipped, el.assembly)
    const stack = { exterior: 0, interior: 0, core: 0 }
    // a NEW finish troweled on in this phase is marked too (green), so it can come back off
    const adds = Object.entries(sc.add ?? {}).map(([face, materialId]) => ({ face, keys: [], verb: 'add', materialId }))
    for (const mark of [...adds, ...wallDemoMarks(el)]) {
      let off = 0
      if (mark.face !== 'core') {
        const o = mark.face === 'exterior' ? faces.exteriorOff : faces.interiorOff
        off = o + Math.sign(o || 1) * 0.25
      }
      const line = offsetPolyline(el.params.path, delta + off)
      const pts = line ? flattenPathPts(line) : []
      if (pts.length < 2) continue
      const p = midOf(pts)
      const label = mark.insulation ? 'Insulation' : materialName(model, mark.materialId)
      out.push({
        id: `${rec.id}|${mark.verb === 'add' ? 'add:' + mark.face : mark.keys.join(',')}`, wallId: rec.id, mark, label,
        world: [p[0], p[1], (zr.z0 + zr.z1) / 2], stack: stack[mark.face]++,
      })
    }
  }
  return out
}
